import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "./firebase";

/**
 * Normalizes phone numbers to standard searchable variants.
 * Handles +91, 0 prefix, spaces, dashes, and extracts last 10 digits.
 */
export const getPhoneSearchVariants = (rawPhone) => {
  if (!rawPhone) return [];
  const digits = String(rawPhone).replace(/\D/g, "");
  if (digits.length < 7) return [rawPhone.trim()];

  const last10 = digits.slice(-10);
  const variants = [
    rawPhone.trim(),
    digits,
    last10,
    `+91${last10}`,
    `+91 ${last10}`,
    `+91 ${last10.slice(0, 5)} ${last10.slice(5)}`,
    `0${last10}`,
    `91${last10}`
  ];

  return Array.from(new Set(variants.filter(Boolean)));
};

/**
 * Checks if a mobile phone number is already registered to another user account.
 * @param {string} phone - Phone number entered by user
 * @param {string} [excludeUid] - Current user UID to exclude from collision (useful for profile update)
 * @returns {Promise<boolean>} - True if taken by another user, false if available
 */
export const isPhoneTaken = async (phone, excludeUid = null) => {
  if (!phone || !db) return false;
  const variants = getPhoneSearchVariants(phone);
  if (variants.length === 0) return false;

  try {
    const usersRef = collection(db, "users");

    // 1. Query 'phone' field across variants (Firestore supports up to 30 in 'in' queries)
    const phoneChunks = [];
    for (let i = 0; i < variants.length; i += 10) {
      phoneChunks.push(variants.slice(i, i + 10));
    }

    for (const chunk of phoneChunks) {
      const qPhone = query(usersRef, where("phone", "in", chunk));
      const snap = await getDocs(qPhone);
      for (const docSnap of snap.docs) {
        if (!excludeUid || docSnap.id !== excludeUid) {
          return true;
        }
      }
    }

    // 2. Query legacy 'mobile' field across variants
    for (const chunk of phoneChunks) {
      const qMobile = query(usersRef, where("mobile", "in", chunk));
      const snap = await getDocs(qMobile);
      for (const docSnap of snap.docs) {
        if (!excludeUid || docSnap.id !== excludeUid) {
          return true;
        }
      }
    }

    return false;
  } catch (err) {
    console.warn("[UserValidation] Error checking phone uniqueness:", err);
    return false;
  }
};

/**
 * Checks if an email is already registered to another user in Firestore.
 * @param {string} email
 * @param {string} [excludeUid]
 * @returns {Promise<boolean>}
 */
export const isEmailTaken = async (email, excludeUid = null) => {
  if (!email || !email.includes("@") || !db) return false;
  const cleanEmail = email.trim().toLowerCase();

  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", cleanEmail));
    const snap = await getDocs(q);
    for (const docSnap of snap.docs) {
      if (!excludeUid || docSnap.id !== excludeUid) {
        return true;
      }
    }
    return false;
  } catch (err) {
    console.warn("[UserValidation] Error checking email uniqueness:", err);
    return false;
  }
};

/**
 * Checks if a GitHub account / username is already registered to another user in Firestore.
 * @param {string} githubIdentifier - GitHub username or GitHub UID
 * @param {string} [excludeUid]
 * @returns {Promise<boolean>}
 */
export const isGithubTaken = async (githubIdentifier, excludeUid = null) => {
  if (!githubIdentifier || !db) return false;
  const clean = String(githubIdentifier).trim().toLowerCase();

  try {
    const usersRef = collection(db, "users");

    // Check githubUsername
    const q1 = query(usersRef, where("githubUsername", "==", clean));
    const snap1 = await getDocs(q1);
    for (const docSnap of snap1.docs) {
      if (!excludeUid || docSnap.id !== excludeUid) return true;
    }

    // Check githubId
    const q2 = query(usersRef, where("githubId", "==", clean));
    const snap2 = await getDocs(q2);
    for (const docSnap of snap2.docs) {
      if (!excludeUid || docSnap.id !== excludeUid) return true;
    }

    return false;
  } catch (err) {
    console.warn("[UserValidation] Error checking github uniqueness:", err);
    return false;
  }
};

export const freeEmailProviders = [
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "rediffmail.com", "icloud.com", "live.com", "msn.com"
];

export const isEducationalEmail = (email) => {
  if (!email || !email.includes("@")) return false;
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return false;
  if (domain.endsWith(".edu") || domain.endsWith(".edu.in") || domain.endsWith(".ac.in")) return true;
  if (domain.includes(".ac.")) return true;
  if (
    domain.includes("university") ||
    domain.includes("college") ||
    domain.includes("institute")
  )
    return true;
  return false;
};

export const isFreeEmailProvider = (email) => {
  if (!email || !email.includes("@")) return false;
  const domain = email.split("@")[1]?.toLowerCase();
  return freeEmailProviders.includes(domain);
};

export const validateEmailForRole = (email, role) => {
  if (!email || !email.includes("@")) {
    return { valid: false, message: "Please enter a valid email address", isEducational: false };
  }
  const isEdu = isEducationalEmail(email);
  const isFree = isFreeEmailProvider(email);

  switch (role) {
    case "Student":
      if (!isFree && !isEdu)
        return {
          valid: false,
          message: "Students must use a recognised free email (gmail, yahoo, outlook, etc.) or educational email",
          isEducational: false,
        };
      return {
        valid: true,
        message: isEdu ? "Educational email verified" : "",
        isEducational: isEdu,
      };

    case "Professional":
      if (isFree)
        return {
          valid: false,
          message: "Professionals must use a corporate/business email (not gmail, yahoo, etc.)",
          isEducational: false,
        };
      return { valid: true, message: "", isEducational: false };

    case "Organisation":
      if (isFree)
        return {
          valid: false,
          message: "Organisations must use an educational or corporate email (not gmail, yahoo, etc.)",
          isEducational: false,
        };
      if (isEdu)
        return { valid: true, message: "Educational email detected", isEducational: true };
      return { valid: true, message: "", isEducational: false };

    default:
      return { valid: true, message: "", isEducational: false };
  }
};

